import { useState, useEffect, useMemo } from 'react'
import { List, Tag, Button, Empty, Loading, NoticeBar, Tabs, Toast } from 'react-vant'
import { ArrowLeft, Volume, CalendarO, MedalO, Arrow } from '@react-vant/icons'
import { useNavigate } from 'react-router-dom'
import request from '../../api/request'

export default function ClassMatch() {
    const navigate = useNavigate()
    const [loading, setLoading] = useState(false)
    const [calendarData, setCalendarData] = useState({})
    const [notices, setNotices] = useState([])

    // 生成标准日历年的12个月 (1月到12月)
    const months = useMemo(() => {
        const result = []
        const currentYear = new Date().getFullYear()
        for (let i = 0; i < 12; i++) {
            const date = new Date(currentYear, i, 1)
            result.push({
                label: `${i + 1}月`,
                year: currentYear,
                month: i,
                id: `${currentYear}-${i}`,
                isHoliday: (i + 1) === 2 || (i + 1) === 8 // 2月和8月是寒暑假
            })
        }
        return result
    }, [])

    const currentMonthIndex = new Date().getMonth()
    const [activeMonthId, setActiveMonthId] = useState(months[currentMonthIndex].id) // 默认选中当前月

    const currentMonthData = useMemo(() => {
        const month = months.find(m => m.id === activeMonthId)
        if (!month) return { start: '', end: '' }

        const start = new Date(month.year, month.month, 1)
        const end = new Date(month.year, month.month + 1, 0)

        return {
            start: start.toISOString().split('T')[0],
            end: end.toISOString().split('T')[0]
        }
    }, [activeMonthId, months])

    const fetchCalendar = async (start, end) => {
        setLoading(true)
        try {
            const data = await request.get('/sports/class-match/calendar/', {
                params: { start_date: start, end_date: end }
            })
            setCalendarData(data || {})
        } catch (e) {
            console.error('获取日历数据失败', e)
        } finally {
            setLoading(false)
        }
    }

    const fetchNotices = async () => {
        try {
            const noticeRes = await request.get('/sports/class-match/notices/active/')
            setNotices(noticeRes || [])
        } catch (e) {
            console.error('获取通知失败', e)
        }
    }

    useEffect(() => {
        fetchNotices()
    }, [])

    useEffect(() => {
        fetchCalendar(currentMonthData.start, currentMonthData.end)
    }, [currentMonthData])

    // 将日历数据转为排序后的数组
    const sortedDates = useMemo(() => {
        return Object.keys(calendarData).sort((a, b) => a.localeCompare(b))
    }, [calendarData])

    return (
        <div className="class-match-page" style={{ background: 'var(--canvas-bg, #f8fafc)', minHeight: '100vh', paddingBottom: 60 }}>
            {/* 星瀚空天青蓝沉浸式页眉 (对标 App 品牌规范) */}
            <div style={{
                background: 'linear-gradient(135deg, #055e43 0%, #076f51 50%, #0c8c68 100%)',
                padding: '44px 16px 20px',
                color: '#fff',
                position: 'relative',
                overflow: 'hidden'
            }}>
                {/* 装饰半透圆 */}
                <div style={{
                    position: 'absolute', top: -30, right: -30,
                    width: 140, height: 140, borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.08)'
                }} />

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
                    <div
                        onClick={() => navigate(-1)}
                        style={{
                            width: 36, height: 36, borderRadius: '50%',
                            background: 'rgba(255,255,255,0.2)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer'
                        }}
                    >
                        <ArrowLeft color="#fff" fontSize={18} />
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#fff', letterSpacing: '-0.2px' }}>班级趣味对抗赛</h1>
                        <p style={{ margin: '2px 0 0', fontSize: 11.5, color: 'rgba(255,255,255,0.85)', fontWeight: 400 }}>全员参与 · 悦动校园 · 赛程积分实时看</p>
                    </div>
                    <div style={{ width: 36 }} />
                </div>
            </div>

            <div style={{ marginTop: 12 }}>
                {/* 滚动通知 */}
                {notices.length > 0 && (
                    <div style={{ padding: '0 16px', marginBottom: 12 }}>
                        <NoticeBar
                            style={{
                                borderRadius: 14,
                                background: '#ffffff',
                                color: '#475569',
                                boxShadow: 'var(--shadow-card, 0 4px 16px rgba(15, 23, 42, 0.05))',
                                border: '1px solid #f1f5f9'
                            }}
                            leftIcon={<Volume color="#ff5e1e" />}
                            text={notices.map(n => n.content).join(' | ')}
                            scrollable
                        />
                    </div>
                )}

                {/* 月份选择器 */}
                <div style={{
                    background: '#ffffff',
                    margin: '0 16px 12px',
                    borderRadius: 16,
                    padding: '4px 0',
                    boxShadow: 'var(--shadow-card, 0 4px 16px rgba(15, 23, 42, 0.05))',
                    border: '1px solid #e2e8f0'
                }}>
                    <Tabs
                        active={activeMonthId}
                        onChange={setActiveMonthId}
                        border={false}
                        type="line"
                        color="#056b4f"
                        titleActiveColor="#056b4f"
                        tabBarGutter={18}
                    >
                        {months.map(m => (
                            <Tabs.TabPane
                                key={m.id}
                                name={m.id}
                                title={
                                    <span style={{ fontSize: 13, color: m.isHoliday ? '#fa8c16' : 'inherit', fontWeight: activeMonthId === m.id ? 700 : 500 }}>
                                        {m.label}
                                        {m.isHoliday && <div style={{ fontSize: 9, marginTop: -4, opacity: 0.8, color: '#fa8c16' }}>假期</div>}
                                    </span>
                                }
                            />
                        ))}
                    </Tabs>
                </div>

                <div style={{ padding: '0 16px' }}>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: 80 }}>
                            <Loading type="spinner" color="#056b4f" vertical>加载赛事中...</Loading>
                        </div>
                    ) : sortedDates.length > 0 ? (
                        <div>
                            {sortedDates.map(dateStr => (
                                <div key={dateStr} style={{ marginBottom: 20 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10, padding: '0 4px' }}>
                                        <div style={{
                                            width: 4, height: 16, background: '#056b4f', borderRadius: 2, marginRight: 8
                                        }} />
                                        <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
                                            {dateStr.split('-')[1]}月{dateStr.split('-')[2]}日
                                        </span>
                                        <span style={{ fontSize: 12, color: '#64748b', marginLeft: 8 }}>
                                            {calendarData[dateStr].total_matches} 场赛事
                                        </span>
                                    </div>

                                    {calendarData[dateStr].matches.map(item => {
                                        const isCompleted = item.status === 'completed'
                                        const isOngoing = item.status === 'ongoing'
                                        return (
                                            <div key={item.id} style={{
                                                background: '#ffffff',
                                                padding: '16px',
                                                borderRadius: 16,
                                                marginBottom: 12,
                                                boxShadow: 'var(--shadow-card, 0 4px 16px rgba(15, 23, 42, 0.05))',
                                                border: '1px solid #f1f5f9'
                                            }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                                    <Tag
                                                        round
                                                        style={{
                                                            background: isCompleted ? '#f1f5f9' : (isOngoing ? '#e8f9f0' : '#f0fdf4'),
                                                            color: isCompleted ? '#64748b' : (isOngoing ? '#00b578' : '#056b4f'),
                                                            border: 'none',
                                                            fontWeight: 600,
                                                            padding: '2px 8px'
                                                        }}
                                                    >
                                                        {isCompleted ? '已结束' : (isOngoing ? '进行中' : '待开始')}
                                                    </Tag>
                                                    <span style={{ fontSize: 13, color: '#056b4f', fontWeight: 600 }}>{item.start_time} 开始</span>
                                                </div>
                                                <h3 style={{ margin: '0 0 10px', fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{item.title}</h3>
                                                <div style={{ marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                                    <span style={{
                                                        background: '#f0fdf4',
                                                        color: '#056b4f',
                                                        fontSize: 11,
                                                        fontWeight: 600,
                                                        padding: '2px 8px',
                                                        borderRadius: 6
                                                    }}>
                                                        {item.grade}
                                                    </span>
                                                    {item.participants?.map(p => {
                                                        const name = typeof p === 'object' ? p.name : p;
                                                        return (
                                                            <span key={name} style={{
                                                                background: '#f8fafc',
                                                                color: '#475569',
                                                                fontSize: 11,
                                                                fontWeight: 500,
                                                                padding: '2px 8px',
                                                                borderRadius: 6,
                                                                border: '1px solid #e2e8f0'
                                                            }}>
                                                                {name}
                                                            </span>
                                                        );
                                                    })}
                                                </div>
                                                <div style={{
                                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                    paddingTop: 12, borderTop: '1px solid #f1f5f9'
                                                }}>
                                                    <div style={{ color: '#64748b', fontSize: 13, display: 'flex', alignItems: 'center' }}>
                                                        <span>📍 {item.venue}</span>
                                                    </div>
                                                    <div
                                                        onClick={() => navigate(`/sports/class-match/${item.id}`)}
                                                        style={{
                                                            display: 'flex', alignItems: 'center', gap: 4,
                                                            color: '#056b4f', fontSize: 13, fontWeight: 700, cursor: 'pointer'
                                                        }}
                                                    >
                                                        查看联赛 <Arrow fontSize={12} />
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div style={{ padding: '60px 0' }}>
                            <Empty description="该月份暂无比赛安排" />
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
